import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Icon } from '@/components/icons';
import { EASE, DURATION } from './motion';

interface ModalProps {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  initialFocusRef?: RefObject<HTMLElement | null>;
  closeOnBackdropClick?: boolean;
  /** Skip the standard header (title + close) so the content owns its layout. */
  hideHeader?: boolean;
  /** Extra classes for the dialog itself. */
  className?: string;
  /** Extra classes for the body wrapper. */
  bodyClassName?: string;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ open, title, description, onClose, children, initialFocusRef, closeOnBackdropClick = true, hideHeader = false, className = '', bodyClassName = '' }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const previousFocus = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    previousFocus.current = document.activeElement as HTMLElement | null;
    const focusTarget = initialFocusRef?.current ?? dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    (focusTarget ?? dialogRef.current)?.focus();
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      previousFocus.current?.focus();
    };
  }, [open, initialFocusRef]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="modal-layer"
          role="presentation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: DURATION.fast }}
        >
          <motion.div
            className="modal-backdrop"
            aria-hidden="true"
            onMouseDown={closeOnBackdropClick ? onClose : undefined}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: DURATION.fast }}
          />
          <motion.div
            ref={dialogRef}
            className={`modal ${className}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={hideHeader ? undefined : titleId}
            aria-describedby={!hideHeader && description ? descriptionId : undefined}
            tabIndex={-1}
            onMouseDown={(event) => event.stopPropagation()}
            initial={{ opacity: 0, y: 12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: DURATION.base, ease: EASE }}
          >
            {!hideHeader && (
              <div className="modal-header">
                <div>
                  <h2 id={titleId} className="modal-title">{title}</h2>
                  {description && <p id={descriptionId} className="modal-description">{description}</p>}
                </div>
                <button className="modal-close" type="button" onClick={onClose} aria-label="Close">
                  <Icon name="close" size={20} aria-hidden="true" />
                </button>
              </div>
            )}
            <div className={`modal-body ${bodyClassName}`}>{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
